import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, CheckCircle2, XCircle, Phone, User, MapPin, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api, setAuthToken } from "../lib/api";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, cancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.get("/bookings"), api.get("/admin/stats")]);
      setBookings(b.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthToken(null);
        navigate("/admin");
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("byt_admin_token")) {
      navigate("/admin");
      return;
    }
    load();
    // Run once on mount; load and navigate are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    setAuthToken(null);
    navigate("/admin");
  };

  const confirmBooking = async () => {
    if (!driverName || !driverPhone) {
      toast.error("Driver name and phone are required");
      return;
    }
    try {
      await api.put(`/bookings/${confirmModal.id}/confirm`, { driver_name: driverName, driver_phone: driverPhone });
      toast.success("Booking confirmed");
      setConfirmModal(null);
      setDriverName("");
      setDriverPhone("");
      load();
    } catch {
      toast.error("Failed to confirm");
    }
  };

  const cancelBooking = async (id) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      await api.put(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch {
      toast.error("Failed to cancel");
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending: "bg-[#FFC300] text-black",
      confirmed: "bg-green-500 text-black",
      cancelled: "bg-red-500 text-white",
      completed: "bg-white text-black",
    };
    return (
      <span className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 ${map[s] || "bg-neutral-700 text-white"}`}>
        {s}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard-page">
      {/* Top bar */}
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/90 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Black Yellow Taxi 24H"
              className="h-10 w-auto"
            />
            <div>
              <div className="font-display text-sm uppercase tracking-tight">Dispatch Console</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFC300]">Black Yellow Taxi 24H</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="refresh-btn"
              onClick={load}
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-widest hover:border-[#FFC300] hover:text-[#FFC300] transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 bg-[#FFC300] text-black font-bold uppercase tracking-widest text-xs px-4 py-2 hover:bg-white transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
          {[
            ["Total", stats.total],
            ["Pending", stats.pending],
            ["Confirmed", stats.confirmed],
            ["Cancelled", stats.cancelled],
          ].map(([k, v]) => (
            <div key={k} className="bg-black p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">{k}</div>
              <div className="font-display text-4xl lg:text-5xl font-black text-white mt-2">{v}</div>
            </div>
          ))}
        </div>

        <h2 className="font-display text-2xl lg:text-3xl uppercase font-black tracking-tighter mb-6">
          All <span className="text-[#FFC300]">Bookings</span>
        </h2>

        {loading ? (
          <div className="text-neutral-400 py-20 text-center">Loading…</div>
        ) : bookings.length === 0 ? (
          <div className="border border-white/10 p-12 text-center text-neutral-400" data-testid="no-bookings">
            No bookings yet.
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((b) => (
              <div key={b.id} className="bg-[#0A0A0A] border border-[#1f1f1f] p-6 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid={`booking-${b.id}`}>
                <div className="lg:col-span-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-[#FFC300]" />
                    <span className="font-bold text-white">{b.customer_name}</span>
                  </div>
                  <a href={`tel:${b.customer_phone}`} className="flex items-center gap-2 text-sm text-neutral-300 hover:text-[#FFC300]">
                    <Phone className="w-3.5 h-3.5" /> {b.customer_phone}
                  </a>
                  <div className="text-xs text-neutral-500 break-all">{b.customer_email}</div>
                </div>

                <div className="lg:col-span-4 space-y-2">
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-3.5 h-3.5 text-[#FFC300] mt-0.5" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">From</div>
                      <div className="text-white">{b.pickup_location}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-3.5 h-3.5 text-[#FFC300] mt-0.5" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">To</div>
                      <div className="text-white">{b.destination}</div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-neutral-300">
                    <Calendar className="w-3.5 h-3.5 text-[#FFC300]" />
                    {new Date(b.pickup_datetime).toLocaleString()}
                  </div>
                  <div className="text-neutral-300">
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500 block">Vehicle</span>
                    {b.vehicle} · {b.passengers} pax
                  </div>
                  {b.driver_name && (
                    <div className="text-neutral-300">
                      <span className="text-[10px] uppercase tracking-widest text-neutral-500 block">Driver</span>
                      {b.driver_name} · {b.driver_phone}
                    </div>
                  )}
                  {b.notes && <div className="text-xs text-neutral-500 italic">"{b.notes}"</div>}
                </div>

                <div className="lg:col-span-2 flex flex-col gap-2 items-start lg:items-end">
                  {statusBadge(b.status)}
                  {b.status === "pending" && (
                    <>
                      <button
                        onClick={() => setConfirmModal(b)}
                        data-testid={`confirm-btn-${b.id}`}
                        className="inline-flex items-center gap-2 bg-[#FFC300] text-black font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-white transition-colors"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Confirm
                      </button>
                      <button
                        onClick={() => cancelBooking(b.id)}
                        data-testid={`cancel-btn-${b.id}`}
                        className="inline-flex items-center gap-2 border border-red-500/40 text-red-400 font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-red-500 hover:text-white transition-colors"
                      >
                        <XCircle className="w-3 h-3" /> Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="confirm-modal">
          <div className="bg-[#0A0A0A] border border-[#FFC300] max-w-md w-full p-8">
            <h3 className="font-display text-2xl uppercase font-black tracking-tighter mb-2">
              Confirm <span className="text-[#FFC300]">Booking</span>
            </h3>
            <p className="text-sm text-neutral-400 mb-6">
              Assigning driver to {confirmModal.customer_name}'s ride.
            </p>
            <label className="block mb-4">
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-400 block mb-1">Driver Name</span>
              <input
                type="text"
                data-testid="driver-name-input"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full"
                placeholder="Carlos Martinez"
              />
            </label>
            <label className="block mb-6">
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-400 block mb-1">Driver Phone</span>
              <input
                type="tel"
                data-testid="driver-phone-input"
                value={driverPhone}
                onChange={(e) => setDriverPhone(e.target.value)}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full"
                placeholder="+34 ..."
              />
            </label>
            <div className="flex gap-3">
              <button
                onClick={confirmBooking}
                data-testid="modal-confirm-btn"
                className="flex-1 bg-[#FFC300] text-black font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-white transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmModal(null)}
                className="border border-white/20 px-4 py-3 text-white font-bold uppercase tracking-widest text-xs hover:border-[#FFC300] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
